/**
 * Active-tracker registry.
 *
 * Exists to give a single ``pagehide`` / ``visibilitychange:hidden``
 * handler authority to finalize every active tracker before the
 * appointment-event queue drains. The previous design — each tracker
 * registers its own ``pagehide`` listener — fired in registration order:
 * the global queue-drain (registered at app boot) ran first against an
 * empty queue, and individual tracker handlers ran later, pushing
 * events that were never flushed under keepalive. Recovery on next load
 * eventually picked them up, but the keepalive design goal was lost
 * and Sentry telemetry showed "never attempted" instead of "attempted
 * via keepalive".
 *
 * With a registry the global handler can iterate every active tracker,
 * synchronously enqueue their finalize events, then drain once — one
 * keepalive batch POST per pagehide regardless of how many tabs the
 * user has visited.
 *
 * The registry is intentionally a separate module to avoid a cycle
 * between ``timeTracker.ts`` (defines TimeTracker) and
 * ``appointmentEvents.ts`` (consumes the registry from its unload
 * handler).
 */
import {
  startClaimResponder,
  stopClaimResponder,
} from "~/services/time-tracking/sessionClaims";
import type { TimeTracker } from "./timeTracker";

const active = new Set<TimeTracker>();

export function registerActiveTracker(tracker: TimeTracker): void {
  active.add(tracker);
  // Answer claim pings for as long as this browser tab holds ANY active
  // tracker — paused ones included, since a paused tracker still owns its
  // localStorage entry and must not be swept by another tab.
  startClaimResponder(activeSessionIds);
}

export function unregisterActiveTracker(tracker: TimeTracker): void {
  active.delete(tracker);
  if (active.size === 0) stopClaimResponder();
}

/**
 * Session ids of every tracker currently active in THIS browser tab.
 *
 * Two readers. The claim responder reports these to other tabs, and the sweep
 * excludes them from its candidates before asking — it can't count on hearing
 * itself: a
 * tab's first tracker opens the responder only after its handoff has pinged,
 * and without ``BroadcastChannel`` there is no responder. That self-accounting
 * also guards
 * the ordering hazard where the recovery sweep runs after the first tracker has
 * already started (the hook's recovery effect waits on the lambda service, the
 * tracking effect does not) — without it, a late sweep would abandon the
 * session it was meant to protect.
 */
export function activeSessionIds(): Set<string> {
  const ids = new Set<string>();
  for (const tracker of active) {
    const id = tracker.currentSessionId;
    if (id !== null) ids.add(id);
  }
  return ids;
}

/** Test seam — drop every registration without finalizing anything. */
export function resetTrackerRegistryForTests(): void {
  active.clear();
}

/**
 * Stops every currently-active tracker with the given outcome and
 * ``keepInStorage: true`` — the keepalive batch POST that follows is
 * best-effort, so the localStorage entries remain as a recovery
 * fallback in case the network kills the request mid-flight.
 *
 * Each ``stopTracking`` call synchronously pushes a single event into
 * the appointment-event queue. The caller is expected to drain the
 * queue immediately after this returns.
 */
export function stopAllActiveTrackers(outcome: string): void {
  for (const tracker of active) {
    if (tracker.isTracking) {
      tracker.stopTracking(outcome, { keepInStorage: true });
    }
  }
}
